import { RedirectPage } from '@carboncopyco/components';
import type { IUser, Tour, TourFrame, TourStep } from '@carboncopyco/types';
import { Drawer as DrawerUIComponent } from '@carboncopyco/ui';
import { type RefObject, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectActiveFrameId } from '../../../store';
import type { UseNavigate } from '../../../types/dependencies';
import { useTourEditorPageProps } from '../TourEditorPageContext';
import type { ReplayerEditorRef } from '../ToursReplayerEditor';
import { FrameList } from './FrameList';
import Settings from './Settings';
import { TitleBar } from './TitleBar';

interface IDrawer {
  frames?: TourFrame[];
  tourId: string;
  tour: Tour;
  user: IUser;
  replayerRef: RefObject<ReplayerEditorRef>;
  demoIdsTourIsBasedOn: number[];
  selectedFrameIndex: number;
  deeplinkProtocol: string;
  workspaceId: string;
  doReplay: (step: TourStep, frameId: number) => void;
  useNavigate: UseNavigate;
  onDrawerClick: () => void;
}

export const Drawer = ({
  frames,
  tourId,
  tour,
  user,
  replayerRef,
  demoIdsTourIsBasedOn,
  selectedFrameIndex,
  doReplay,
  useNavigate,
  deeplinkProtocol,
  onDrawerClick,
  workspaceId,
}: IDrawer) => {
  const { uuid } = useTourEditorPageProps();
  const activeFrameId = useSelector(selectActiveFrameId(uuid));
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const [appDeeplink, setAppDeeplink] = useState('');

  const openRedirectPage = (url: string) => {
    setAppDeeplink(url);
  };

  if (frames?.length === 0) {
    return null;
  }

  return (
    <div
      className="relative w-full h-full"
      onClick={onDrawerClick}
    >
      <DrawerUIComponent
        noScroll
        isOpen
        left
        width={350}
        header={
          <TitleBar
            tourId={tourId}
            frames={frames}
            selectedFrameIndex={selectedFrameIndex}
            onSettingsButtonClick={() => setIsSettingsOpen(true)}
            demoIdsTourIsBasedOn={demoIdsTourIsBasedOn}
            deeplinkProtocol={deeplinkProtocol}
            tour={tour}
            useNavigate={useNavigate}
            openRedirectPage={openRedirectPage}
          />
        }
      >
        <div className="h-full pl-5 pb-11">
          <FrameList
            tourId={tourId}
            frames={frames}
            fileServiceApiToken={tour?.fileServiceToken}
            activeFrameId={activeFrameId}
            replayerRef={replayerRef}
            doReplay={(step: TourStep) => doReplay(step, activeFrameId)}
          />
        </div>
        <Settings
          workspaceId={workspaceId}
          tourId={tourId}
          frames={frames}
          user={user}
          isSettingsOpen={isSettingsOpen}
          replayerRef={replayerRef}
          onSettingsClose={() => setIsSettingsOpen(false)}
          fileServiceToken={tour?.fileServiceToken}
        />
      </DrawerUIComponent>
      {appDeeplink && (
        <div className="fixed h-screen w-screen border z-9999 top-0 bg-white">
          <RedirectPage
            appDeeplink={appDeeplink}
            resource="tour"
          />
        </div>
      )}
    </div>
  );
};
